import React from "react";
import * as io from "io-ts";
import { buildOptions, FilterWrapper, useSubStateSetter } from "@ui/Filter";
import Reset from "@ui/Filter/Reset";
import FilterSelect from "@ui/Filter/FilterSelect";
import Switch from "@ui/Switch";
import createGlobalState from "@ui/lib/createGlobalState";
import t from "../../i18n";
import { activityOptionsFilter, ActivityTypeValidator } from "../Activity/activityLabels";
import { useProjects } from "../../api/useProjects";
import { useAuthors } from "../../api/useAuthors";
import { formatAuthorName } from "../Activity/getActivityDescription";
import { uuid } from "diagnostics-lib/io";
import { useTranslations } from "../../api/useTranslations";

const FilteredValuesByFilterType = io.strict({
  authors: io.array(io.string),
  projects: io.array(uuid),
  types: io.array(ActivityTypeValidator),
});
type FilteredValuesByFilterType = io.TypeOf<typeof FilteredValuesByFilterType>;

const DEFAULT_FILTER_VALUES: FilteredValuesByFilterType = {
  authors: [],
  projects: [],
  types: [],
};

export const useFilteredValuesByFilterType = createGlobalState({
  storage: "session",
  key: "globalActivityFilteredValues",
  type: FilteredValuesByFilterType,
  defaultValue: DEFAULT_FILTER_VALUES,
  scoped: false,
});

export const useHideAutomaticActions = createGlobalState({
  storage: "session",
  key: "globalHideAutomaticActions",
  type: io.boolean,
  defaultValue: true,
  scoped: false,
});

interface P {
  filteredValues: FilteredValuesByFilterType;
  setFilteredValues: (props: React.SetStateAction<FilteredValuesByFilterType>) => void;
  hideSystemUserActions: boolean;
  setHideAutomaticActions: (value: boolean) => void;
}

const GlobalActivityFilters = ({
  filteredValues,
  setFilteredValues,
  hideSystemUserActions,
  setHideAutomaticActions,
}: P) => {
  const translations = useTranslations();
  const projects = useProjects();
  const projectOptions = React.useMemo(
    () => buildOptions(projects.map(p => ({ label: p.name, value: p.id.toString() }))),
    [projects],
  );
  const users = useAuthors();
  const userOptions = React.useMemo(
    () =>
      buildOptions(
        users.map(user => ({
          value: user.id,
          label: formatAuthorName(user.name),
          badge: user.deletedAt === null ? undefined : t("deleted"),
        })),
      ),
    [users],
  );

  return (
    <>
      <FilterWrapper>
        <FilterSelect
          title={t("activity.selectFilter.projects")}
          options={projectOptions}
          values={filteredValues.projects}
          setValues={useSubStateSetter(setFilteredValues, "projects")}
          filtered
        />
        <FilterSelect
          title={t("activity.selectFilter.activity")}
          options={activityOptionsFilter(translations)}
          values={filteredValues.types}
          setValues={useSubStateSetter(setFilteredValues, "types")}
          filtered
        />
        <FilterSelect
          title={t("activity.selectFilter.authors")}
          options={userOptions}
          values={filteredValues.authors}
          setValues={useSubStateSetter(setFilteredValues, "authors")}
          filtered
        />
        <Reset
          filterValues={filteredValues}
          setFilterValues={setFilteredValues}
          defaultFilterValues={DEFAULT_FILTER_VALUES}
        />
      </FilterWrapper>
      <Switch
        checked={hideSystemUserActions}
        onChange={e => setHideAutomaticActions(e.currentTarget.checked)}
        tooltipOverlay={t("activity.hide_automatic_actions.tooltip")}
      >
        {t("activity.hide_automatic_actions")}
      </Switch>
    </>
  );
};

export default GlobalActivityFilters;
